import {SiteModel, siteModelFactory} from "./site.model";
import {PercentageBarModel, percentageBarModelFactory} from "../misc/percentage-bar.model";
import {ContactModel, contactModelFactory} from "../contact/contact.model";
import {toBoolean, toList, toString} from "../helpers/helpers";

export interface SiteCdfModel {
  system: string;
  id: string;
  buildings: string[];
}

export interface SiteCdfBuildingModel {
  index: string;
  building: string;
  site: string;
  floor: string;
  selected: boolean;
}

export function siteCdfBuildingModelFactory(json: any): SiteCdfBuildingModel {
  json = json || {};

  return {
    index: toString(json.index),
    building: toString(json.building),
    site: toString(json.site),
    floor: toString(json.floor),
    selected: toBoolean(json.selected),
  } as SiteCdfBuildingModel;
}

export function siteCdfModelFactory(json: any): SiteCdfModel {
  json = json || {};

  return {
    system: toString(json.system),
    id: toString(json.id),
    buildings: toList(toString, json.buildings),
  } as SiteCdfModel;
}

export interface SiteDetailModel extends SiteModel {
  deploymentCount?: number;
  orderCount?: number;
  requestedOrdersCount?: number;
  plannedDevices?: number;
  deployedDevices?: number;
  undeployedDevices?: number;
  orderedDevices?: number;
  workflowId?: string;
  city?: string;
  region?: string;
  site_contact_id?: string;
  delivery_contact_id?: string;
  installation_contact_id?: string;
  toner_contact_id?: string;
  it_contact_id?: string;
  manager_contact_id?: string;
  alternate_contact_id?: string;
  deployedToK2PercentageBar?: PercentageBarModel;
  pastSchedule?: boolean;
  pastScheduleMessage?: string;
  siteContact?: ContactModel;
  deliveryContact?: ContactModel;
  installationContact?: ContactModel;
  tonerContact?: ContactModel;
  itContact?: ContactModel;
  managerContact?: ContactModel;
  alternateContact?: ContactModel;
  breakfixContact?: ContactModel;
  orderStatusNotification1?: ContactModel;
  orderStatusNotification2?: ContactModel;
  siteNameStructure?: string;
  partnerPurchaseOrders?: any;
  cdfs: SiteCdfModel[];
  estimatedSiteSowSigned?: string;
  actualSiteSowSigned?: string;
  estimatedSiteDiscovery?: string;
  actualSiteDiscovery?: string;
  estimatedSiteDesignApproval?: string;
  actualSiteDesignApproval?: string;
  estimatedSiteMonitoringEnabled?: string;
  actualSiteMonitoringEnabled?: string;
  estimatedSiteReadyToDeploy?: string;
  actualSiteReadyToDeploy?: string;
  estimatedSiteDeploymentStart?: string;
  actualSiteDeploymentStart?: string;
  estimatedSiteDeploymentEnd?: string;
  actualSiteDeploymentEnd?: string;
  estimatedSiteCustomerAcceptance?: string;
  actualSiteCustomerAcceptance?: string;
  estimatedSiteHandover?: string;
  actualSiteHandover?: string;
  hpInstalledKit?: boolean;
  businessPartnerId?: string;
  isMigrationActive?: boolean;
}

function toNumber(obj): number {
  const f = parseFloat(obj);
  if (Number.isNaN(f)) return undefined;
  return f;
}

function parseDateFromJSON(date) {
  if (!date) return "";
  const matches = `${date}`.match(/^([0-9]{4}-[0-9]{2}-[0-9]{2})T?/i);
  if (!matches) return "";
  return matches[1] || "";
}

export function siteDetailModelFactory(json?: any): SiteDetailModel {
  json = json || {};
  const base = siteModelFactory(json);

  return {
    ...base,
    deploymentCount: toNumber(json.deploymentCount) || 0,
    orderCount: toNumber(json.orderCount) || 0,
    requestedOrdersCount: toNumber(json.requestedOrdersCount) || 0,
    plannedDevices: toNumber(json.plannedDevices) || 0,
    deployedDevices: toNumber(json.deployedDevices) || 0,
    undeployedDevices: toNumber(json.undeployedDevices) || 0,
    orderedDevices: toNumber(json.orderedDevices) || 0,
    workflowId: json.workflowId,
    city: json.city || "",
    region: json.region || "",
    site_contact_id: json.site_contact_id, // eslint-disable-line @typescript-eslint/camelcase
    delivery_contact_id: json.site_contact_id, // eslint-disable-line @typescript-eslint/camelcase
    installation_contact_id: json.site_contact_id, // eslint-disable-line @typescript-eslint/camelcase
    toner_contact_id: json.site_contact_id, // eslint-disable-line @typescript-eslint/camelcase
    it_contact_id: json.site_contact_id, // eslint-disable-line @typescript-eslint/camelcase
    manager_contact_id: json.site_contact_id, // eslint-disable-line @typescript-eslint/camelcase
    alternate_contact_id: json.site_contact_id, // eslint-disable-line @typescript-eslint/camelcase
    deployedToK2PercentageBar: percentageBarModelFactory(json.deployedToK2PercentageBar),
    pastSchedule: !!json.pastSchedule,
    pastScheduleMessage: json.pastScheduleMessage || "",
    siteContact: contactModelFactory(json.siteContact),
    deliveryContact: contactModelFactory(json.deliveryContact),
    installationContact: contactModelFactory(json.installationContact),
    tonerContact: contactModelFactory(json.tonerContact),
    itContact: contactModelFactory(json.itContact),
    managerContact: contactModelFactory(json.managerContact),
    alternateContact: contactModelFactory(json.alternateContact),
    breakfixContact: contactModelFactory(json.breakfixContact),
    orderStatusNotification1: contactModelFactory(json.orderStatusNotification1),
    orderStatusNotification2: contactModelFactory(json.orderStatusNotification2),
    siteNameStructure: json.siteNameStructure || "",
    partnerPurchaseOrders: json.partnerPurchaseOrders || [],
    cdfs: toList(siteCdfModelFactory, json.cdfs),
    estimatedSiteSowSigned: parseDateFromJSON(json.estimatedSiteSowSigned),
    actualSiteSowSigned: parseDateFromJSON(json.actualSiteSowSigned),
    estimatedSiteDiscovery: parseDateFromJSON(json.estimatedSiteDiscovery),
    actualSiteDiscovery: parseDateFromJSON(json.actualSiteDiscovery),
    estimatedSiteDesignApproval: parseDateFromJSON(json.estimatedSiteDesignApproval),
    actualSiteDesignApproval: parseDateFromJSON(json.actualSiteDesignApproval),
    estimatedSiteMonitoringEnabled: parseDateFromJSON(json.estimatedSiteMonitoringEnabled),
    actualSiteMonitoringEnabled: parseDateFromJSON(json.actualSiteMonitoringEnabled),
    estimatedSiteReadyToDeploy: parseDateFromJSON(json.estimatedSiteReadyToDeploy),
    actualSiteReadyToDeploy: parseDateFromJSON(json.actualSiteReadyToDeploy),
    estimatedSiteDeploymentStart: parseDateFromJSON(json.estimatedSiteDeploymentStart),
    actualSiteDeploymentStart: parseDateFromJSON(json.actualSiteDeploymentStart),
    estimatedSiteDeploymentEnd: parseDateFromJSON(json.estimatedSiteDeploymentEnd),
    actualSiteDeploymentEnd: parseDateFromJSON(json.actualSiteDeploymentEnd),
    estimatedSiteCustomerAcceptance: parseDateFromJSON(json.estimatedSiteCustomerAcceptance),
    actualSiteCustomerAcceptance: parseDateFromJSON(json.actualSiteCustomerAcceptance),
    estimatedSiteHandover: parseDateFromJSON(json.estimatedSiteHandover),
    actualSiteHandover: parseDateFromJSON(json.actualSiteHandover),
    hpInstalledKit: toBoolean(json.hpInstalledKit, true),
    businessPartnerId: json.businessPartnerId || "",
    isMigrationActive: json.isMigrationActive || false,
  } as SiteDetailModel;
}

export function siteDetailModelListFactory(json?: any): SiteDetailModel[] {
  return Array.from(json || []).map(site => siteDetailModelFactory(site));
}
